import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  GetTenantDocument,
  UpdateTenantProductVisibilityDocument,
  type UpdateTenantProductVisibilityMutation,
  type UpdateTenantProductVisibilityMutationVariables,
} from "~/features/admin/tenants/gqls";
import type { TenantProductVisibility } from "~/features/admin/tenants/types";
import { translateError } from "~/lib/errors";

interface UseTenantUpdateProductVisibilityOptions {
  onSuccess?: () => void;
}

/** Change the visibility of an installed product connection. */
export function useTenantUpdateProductVisibility({ onSuccess }: UseTenantUpdateProductVisibilityOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [updateVisibility] = useMutation<
    UpdateTenantProductVisibilityMutation,
    UpdateTenantProductVisibilityMutationVariables
  >(UpdateTenantProductVisibilityDocument, { refetchQueries: [GetTenantDocument], awaitRefetchQueries: true });

  const submit = useCallback(
    async (tenantId: string, productId: string, visibility: TenantProductVisibility) => {
      setProcessing(true);
      try {
        const res = await updateVisibility({ variables: { input: { id: tenantId, productId, visibility } } });
        match(res.data?.cnutUpdateTenantProductVisibility)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "TenantUpdateProductVisibilityOutput" }, () => {
            toast.success(t("model.tenant.visibilityUpdatedSuccessfully"));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [updateVisibility, t, onSuccess],
  );

  return { submit, processing };
}
